import React from 'react';
import classNames from 'classnames';
import './Label.less';
import { DataAttributeProps, getDataAttributes } from 'utils/DataAttributeUtils';
import { ValidationSummary } from 'utils/ValidationUtils';
import { getValidationTextClass } from './Validation/helpers/ValidationCssHelpers';

export type VerticalPlacement = 'top' | 'middle' | 'bottom';
export type HorizontalPlacement = 'left' | 'right' | 'center';
export interface Placement {
  horizontal: HorizontalPlacement;
  vertical: VerticalPlacement;
}
export interface ILabelProps extends DataAttributeProps, React.HTMLProps<{}> {
  actions?: React.ReactNode;
  error?: boolean;
  /**
   * By default, this will wrap contents in the label, for the desired
   * behavior that clicking the label will give focus to the associated input.
   * If this is _not_ desired, then add this flag avoid wrapping contents
   * in a the label element.
   */
  doNotWrap?: boolean;
  labelTextClassName?: string;
  validation?: ValidationSummary;
  placement?: Placement;
}

// TODO further alignment of label using flex-box classes
const getClassName = (className?: string, placement?: Placement) =>
  classNames('dpc-field-label', className, {
    'dpc-field-label__align-left-top': placement?.horizontal === 'left' && placement?.vertical === 'top',
    'dpc-field-label__align-left-middle': placement?.horizontal === 'left' && placement?.vertical === 'middle',
    'dpc-field-label__align-left-bottom': placement?.horizontal === 'left' && placement?.vertical === 'bottom',
    'dpc-field-label__align-right-top': placement?.horizontal === 'right' && placement?.vertical === 'top',
    'dpc-field-label__align-right-middle': placement?.horizontal === 'right' && placement?.vertical === 'middle',
    'dpc-field-label__align-right-bottom': placement?.horizontal === 'right' && placement?.vertical === 'bottom',
    'dpc-field-label__align-center-top': placement?.horizontal === 'center' && placement?.vertical === 'top',
    'dpc-field-label__align-center-middle': placement?.horizontal === 'center' && placement?.vertical === 'middle',
    'dpc-field-label__align-center-bottom': placement?.horizontal === 'center' && placement?.vertical === 'bottom',
  });

const getTextClassName = (validation?: ValidationSummary, labelTextClassName?: string) =>
  classNames('dpc-field-label__text', getValidationTextClass(validation), labelTextClassName);

/**
 * Input label component. By default this will wrap contents in a label
 * element, but can be configured to use a section, and embed the label,
 * using the `doNotWrap` prop.
 *
 * This allows it to be used for formatting without passing mouse-events
 * on to the wrapped input, or when one label wraps multiple inputs.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function Label({
  actions,
  children,
  className,
  dataLabel,
  dataPath,
  doNotWrap,
  height,
  htmlFor,
  label,
  labelTextClassName,
  placement,
  validation,
}: ILabelProps) {
  const dataAttrs = getDataAttributes({ dataLabel, dataPath });

  return doNotWrap ? (
    <section className={getClassName(className, placement)} {...dataAttrs} style={{ height }}>
      <span className="dpc-field-label__text-wrapper">
        <label className={getTextClassName(validation, labelTextClassName)} title={label} htmlFor={htmlFor}>
          {label}
        </label>
        {actions}
      </span>
      {children}
    </section>
  ) : (
    <label className={getClassName(className, placement)} htmlFor={htmlFor} {...dataAttrs} style={{ height }}>
      <span className="dpc-field-label__text-wrapper">
        <span className={getTextClassName(validation, labelTextClassName)} title={label}>
          {label}
        </span>
        {actions}
      </span>
      {children}
    </label>
  );
}
